<template>
  <app-page labelledby="home-overview-title" full-width>
    <page-header
      title-id="home-overview-title"
      :title="orgInfo.name || 'Обзор организации'"
      description="Ключевые показатели, рабочие исключения и последние события."
    >
      <template #status>
        <span
          class="freshness-badge"
          :class="`freshness-badge--${financeFreshnessState}`"
          :data-state="financeFreshnessState"
          data-testid="finance-freshness"
        >
          {{ financeFreshnessLabel }}
        </span>
      </template>
      <template #metadata>
        <div class="overview-context" aria-label="Контекст обзора">
          <span><strong>Организация:</strong> {{ orgInfo.name || 'загружается' }}</span>
          <span v-if="hasFinanceOverviewAccess"><strong>Период:</strong> {{ selectedPeriodLabel }}</span>
          <span><strong>Данные:</strong> {{ financeFreshnessDetail }}</span>
        </div>
      </template>
      <template #actions>
        <div v-if="hasFinanceOverviewAccess" class="overview-filters" aria-label="Период обзора">
          <q-select
            v-model="selectedYear"
            outlined
            dense
            options-dense
            :options="yearOptions"
            label="Год"
            class="overview-filter"
          />
          <q-select
            v-model="selectedQuarter"
            outlined
            dense
            options-dense
            emit-value
            map-options
            :options="quarterOptions"
            label="Период"
            class="overview-filter overview-filter--period"
          />
        </div>
      </template>
    </page-header>

    <app-state
      v-if="isLoading"
      state="loading"
      title="Загружаем обзор"
      message="Получаем данные организации и доступные рабочие разделы."
      data-testid="home-overview-loading"
    />

    <template v-else>
      <page-section
        v-if="hasFinanceOverviewAccess"
        title="Ключевые показатели"
        :description="`Показатели за период «${selectedPeriodLabel}».`"
        data-testid="finance-summary-section"
      >
        <div v-if="financeError" class="overview-message overview-message--error" role="alert" data-testid="finance-error">
          <q-icon name="error_outline" aria-hidden="true" />
          <div class="overview-message__content">
            <strong>Финансовые данные недоступны</strong>
            <span>{{ financeError }}</span>
          </div>
          <q-btn flat no-caps color="negative" label="Повторить" :disable="financeLoading" @click="loadFinanceData" />
        </div>

        <div
          v-else-if="financeSummary && financeSummary.is_stale"
          class="overview-message overview-message--warning"
          role="status"
          data-testid="finance-stale"
        >
          <q-icon name="warning_amber" aria-hidden="true" />
          <div class="overview-message__content">
            <strong>Данные требуют формирования</strong>
            <span>{{ staleMessage }}</span>
          </div>
          <q-btn
            unelevated
            no-caps
            color="primary"
            :label="financeRebuildLoading ? 'Формируется' : 'Сформировать'"
            :loading="financeRebuildLoading"
            :disable="financeLoading || financeRebuildLoading"
            @click="rebuildFinanceAggregates"
          />
        </div>

        <div
          v-else-if="financeSummary && !hasSelectedFinanceMovement"
          class="overview-message overview-message--neutral"
          role="status"
          data-testid="finance-no-movement"
        >
          <q-icon name="info_outline" aria-hidden="true" />
          <div class="overview-message__content">
            <strong>За период нет финансовых движений</strong>
            <span>Данные подготовлены, значения равны нулю; график не строится.</span>
          </div>
        </div>

        <div v-if="financeLoading && !financeSummary" class="kpi-grid" data-testid="finance-kpi-loading" role="status" aria-label="Загружаем ключевые показатели">
          <div v-for="index in 5" :key="index" class="kpi-item kpi-item--loading" aria-hidden="true">
            <q-skeleton type="text" width="64%" />
            <q-skeleton type="text" width="86%" height="2rem" />
            <q-skeleton type="text" width="48%" />
          </div>
        </div>
        <div v-else class="kpi-grid" data-testid="finance-kpi-grid">
          <article v-for="card in kpiCards" :key="card.key" class="kpi-item" :data-kpi="card.key">
            <span class="kpi-item__label">{{ card.label }}</span>
            <strong class="kpi-item__value" :class="{ 'kpi-item__value--negative': card.value < 0 }">
              {{ card.display }}
            </strong>
            <span class="kpi-item__period">{{ selectedPeriodLabel }}</span>
            <span class="kpi-item__state">{{ kpiStateLabel(card) }}</span>
          </article>
        </div>
      </page-section>

      <page-section
        v-if="hasOperationalOverview"
        title="Требует внимания"
        :description="attentionSummary"
        data-testid="attention-section"
      >
        <div
          v-if="hasStockAccess"
          class="warehouse-overview"
          data-testid="warehouse-home-section"
          aria-label="Состояние склада"
        >
          <div class="warehouse-overview__header">
            <div>
              <strong>Состояние склада</strong>
              <span>Краткая сводка перед рабочими исключениями</span>
            </div>
            <q-spinner
              v-if="warehouseWidgetsLoading"
              color="primary"
              size="1.25rem"
              data-testid="warehouse-widgets-loading"
            />
          </div>

          <div v-if="warehouseError" class="overview-message overview-message--error" role="alert" data-testid="warehouse-home-error">
            <q-icon name="error_outline" aria-hidden="true" />
            <div class="overview-message__content">
              <strong>Складские данные недоступны</strong>
              <span>{{ warehouseError }}</span>
            </div>
          </div>

          <div class="warehouse-balance" data-testid="warehouse-widget-balances">
            <div>
              <span class="warehouse-balance__label">Остатки склада</span>
              <template v-if="warehouseBalances.length">
                <strong class="warehouse-balance__value">{{ warehousePositionCountLabel }}</strong>
                <span class="warehouse-balance__meta" data-testid="warehouse-widget-total-quantity">Количество: {{ warehouseTotalQuantity }}</span>
                <span class="warehouse-balance__meta" data-testid="warehouse-widget-total-cost">Оценка: {{ warehouseTotalCost }}</span>
              </template>
              <span v-else class="warehouse-balance__empty" data-testid="warehouse-widget-balances-empty">
                {{ warehouseWidgetsLoading ? 'Загрузка остатков' : 'Остатков нет.' }}
              </span>
            </div>
            <q-btn flat no-caps color="primary" label="Открыть остатки" :to="{ name: 'store_balances' }" />
          </div>
        </div>

        <div class="attention-list" role="list">
          <router-link
            v-if="hasStockAccess && stockDiagnosticIssueCount > 0"
            :to="{ name: 'store_settings' }"
            class="attention-item attention-item--danger"
            role="listitem"
            data-testid="warehouse-widget-diagnostics"
          >
            <span class="attention-item__marker" aria-hidden="true" />
            <span class="attention-item__body">
              <strong>Проблемы склада</strong>
              <span>{{ stockDiagnosticIssues.slice(0, 2).map((item) => item.message).join(' · ') }}</span>
            </span>
            <span class="attention-item__count" data-testid="warehouse-widget-diagnostics-count">{{ stockDiagnosticIssueCount }}</span>
            <span class="attention-item__action">Открыть</span>
          </router-link>
          <div
            v-else-if="hasStockAccess"
            class="attention-item attention-item--ok"
            role="listitem"
            data-testid="warehouse-widget-diagnostics-empty"
          >
            <q-icon name="verified" aria-hidden="true" />
            <span class="attention-item__body">
              <strong>Проблемы склада</strong>
              <span>{{ warehouseWidgetsLoading ? 'Загрузка диагностики' : 'Проблем не найдено.' }}</span>
            </span>
          </div>

          <router-link
            v-if="hasStockAccess && lowStockRows.length"
            :to="{ name: 'store_reports' }"
            class="attention-item attention-item--warning"
            role="listitem"
            data-testid="warehouse-widget-low-stock"
          >
            <span class="attention-item__marker" aria-hidden="true" />
            <span class="attention-item__body">
              <strong>Низкие остатки</strong>
              <span>{{ lowStockRows.slice(0, 3).map((row) => row.name).join(', ') }}</span>
            </span>
            <span class="attention-item__count">{{ lowStockRows.length }}</span>
            <span class="attention-item__action">Открыть</span>
          </router-link>
          <div
            v-else-if="hasStockAccess"
            class="attention-item attention-item--ok"
            role="listitem"
            data-testid="warehouse-widget-low-stock-empty"
          >
            <q-icon name="check_circle" aria-hidden="true" />
            <span class="attention-item__body">
              <strong>Низкие остатки</strong>
              <span>{{ warehouseWidgetsLoading ? 'Загрузка остатков' : 'Низких остатков нет.' }}</span>
            </span>
          </div>

          <router-link
            v-if="hasFinDocumentAccess && draftTransfers.length"
            :to="{ name: 'transfers_list' }"
            class="attention-item attention-item--warning"
            role="listitem"
            data-testid="draft-transfers-attention"
          >
            <span class="attention-item__marker" aria-hidden="true" />
            <span class="attention-item__body">
              <strong>Непроведенные проводки</strong>
              <span>Черновики за {{ selectedYear }} год требуют проверки.</span>
            </span>
            <span class="attention-item__count">{{ draftTransfers.length }}</span>
            <span class="attention-item__action">Открыть</span>
          </router-link>
          <div v-else-if="hasFinDocumentAccess && transfersError" class="attention-item attention-item--danger" role="listitem">
            <q-icon name="error_outline" aria-hidden="true" />
            <span class="attention-item__body">
              <strong>Проводки недоступны</strong>
              <span>{{ transfersError }}</span>
            </span>
          </div>

          <template v-for="task in reportingTasks.slice(0, 3)" :key="`${task.title}-${task.dueDate}`">
            <router-link
              v-if="task.routeName"
              :to="{ name: task.routeName }"
              class="attention-item attention-item--info"
              role="listitem"
            >
              <span class="attention-item__marker" aria-hidden="true" />
              <span class="attention-item__body">
                <strong>{{ task.title }}</strong>
                <span>{{ task.period }} · до {{ formatDate(task.dueDate) }} · {{ task.sourceName }}</span>
              </span>
              <span class="attention-item__action">Открыть</span>
            </router-link>
            <div v-else class="attention-item attention-item--neutral" role="listitem">
              <q-icon name="event" aria-hidden="true" />
              <span class="attention-item__body">
                <strong>{{ task.title }}</strong>
                <span>{{ task.period }} · до {{ formatDate(task.dueDate) }} · форма не настроена</span>
              </span>
            </div>
          </template>

          <div v-if="attentionItemCount === 0" class="attention-empty" role="listitem">
            <q-icon name="task_alt" aria-hidden="true" />
            <div>
              <strong>Нет задач, требующих внимания</strong>
              <span>По доступным данным исключений не найдено.</span>
            </div>
          </div>
        </div>
      </page-section>

      <page-section
        v-if="hasRecentEventsAccess"
        title="Последние события"
        :description="recentEventsSummary"
        data-testid="recent-events-section"
      >
        <details class="recent-events" open>
          <summary>Показано событий: {{ recentEventCount }}</summary>
          <div class="recent-events__list">
            <template v-if="recentDocuments.length">
              <button
                v-for="document in recentDocuments.slice(0, 5)"
                :key="`document-${document.id}`"
                type="button"
                class="recent-event"
                @click="openDocument(document)"
              >
                <span class="recent-event__icon" aria-hidden="true"><q-icon name="description" /></span>
                <span class="recent-event__body">
                  <strong>{{ document.name }}</strong>
                  <span>{{ document.date }} · {{ document.direction_display || directionLabel(document.direction) }}</span>
                </span>
                <span class="recent-event__meta">{{ documentTypeLabel(document.document_type) }} · {{ formatMoney(document.summ_total || document.summ, true) }}</span>
              </button>
            </template>
            <div v-else-if="documentsLoading" class="recent-event recent-event--status" role="status">
              <q-spinner size="1rem" />
              <span>Загружаем последние документы</span>
            </div>
            <div v-else-if="hasFinDocumentAccess" class="recent-event recent-event--status">
              <q-icon name="description" aria-hidden="true" />
              <span>{{ documentsError || 'Активных документов за выбранный год нет.' }}</span>
            </div>

            <div data-testid="warehouse-widget-movements">
              <template v-if="recentStockMovements.length">
                <button
                  v-for="movement in recentStockMovements"
                  :key="`movement-${movement.transfer_id || movement.document_id}`"
                  type="button"
                  class="recent-event"
                  @click="openStockMovement(movement)"
                >
                  <span class="recent-event__icon" aria-hidden="true"><q-icon name="sync_alt" /></span>
                  <span class="recent-event__body">
                    <strong>{{ movement.operation_label || movement.operation || 'Движение склада' }}</strong>
                    <span>{{ movement.name }} · {{ movement.incoming_quantity || movement.outgoing_quantity || movement.quantity_delta || 0 }}</span>
                  </span>
                  <span class="recent-event__meta">{{ formatDate(movement.date) }}</span>
                </button>
              </template>
              <div v-else-if="hasStockAccess" class="recent-event recent-event--status" data-testid="warehouse-widget-movements-empty">
                <q-icon name="sync_alt" aria-hidden="true" />
                <span>{{ warehouseWidgetsLoading ? 'Загрузка движений' : 'Движений нет.' }}</span>
              </div>
            </div>
          </div>
        </details>
      </page-section>

      <page-section
        v-if="hasFinanceOverviewAccess"
        title="Финансовая динамика"
        :description="`Сравнение доходов и расходов по месяцам за период «${selectedPeriodLabel}».`"
        data-testid="finance-analysis-section"
      >
        <div class="analysis-panel">
          <div v-if="financeLoading && !financeSummary" class="chart-skeleton" role="status" aria-label="Загружаем финансовую динамику">
            <q-skeleton type="rect" height="16rem" />
          </div>
          <div v-else-if="chartHasData" class="chart-figure" role="img" :aria-label="chartSummary" data-testid="finance-chart-figure">
            <p :class="isCompactViewport ? 'chart-summary-visible' : 'visually-hidden'" data-testid="finance-chart-summary">
              {{ chartSummary }}
            </p>
            <div v-if="!isCompactViewport" ref="financeChart" class="finance-chart" aria-hidden="true" />
          </div>
          <div v-else class="chart-empty" data-testid="finance-chart-empty">
            <q-icon name="bar_chart" aria-hidden="true" />
            <div>
              <strong>График не построен</strong>
              <span>{{ financeError ? 'Финансовые данные недоступны.' : 'За выбранный период нет движений для корректного сравнения.' }}</span>
            </div>
          </div>
        </div>
      </page-section>
    </template>
  </app-page>
</template>

<script>
import { formatMoney as formatDisplayMoney } from '../../services/moneyFormat'
import ApexCharts from 'apexcharts'
import moment from 'moment'
import { AppPage, AppState, PageHeader, PageSection } from '../../components/ui'
import { getUpcomingReportingTasks } from './reportingCalendar'
import { formatDate as formatDisplayDate, formatDateTime as formatDisplayDateTime } from '../../services/dateFormat'

const MONTH_LABELS = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек']

const QUARTER_MONTHS = {
  all: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  q1: [1, 2, 3],
  q2: [4, 5, 6],
  q3: [7, 8, 9],
  q4: [10, 11, 12]
}

const DOCUMENT_ROUTES = {
  upd: 'document_info_upd',
  payment: 'document_info_payment',
  avance: 'document_info_factura_avance',
  factura_avance: 'document_info_factura_avance',
  accept_factura_avance: 'document_info_factura_avance',
  bill_to_pay: 'document_info_bill_to_pay',
  act_sver: 'document_info_act_sver'
}

const DOCUMENT_TYPE_LABELS = {
  upd: 'УПД',
  payment: 'Платеж',
  avance: 'Аванс',
  factura_avance: 'Счет-фактура',
  accept_factura_avance: 'Зачет аванса',
  bill_to_pay: 'Счет',
  act_sver: 'Акт сверки'
}

const PERMISSION_KEYS = ['open_fin_documents', 'change_billing', 'edit_fin_documents', 'edit_org']
const STOCK_PERMISSION_KEYS = ['open_stock', 'edit_stock']

export default {
  name: 'home_overview',
  components: { AppPage, AppState, PageHeader, PageSection },
  data() {
    const currentYear = new Date().getFullYear()
    return {
      isLoading: true,
      orgInfo: {},
      userRole: null,
      permissions: {},
      selectedYear: currentYear,
      selectedQuarter: 'all',
      financeSummary: null,
      financeLoading: false,
      financeRebuildLoading: false,
      financeError: null,
      financeChart: null,
      isCompactViewport: typeof window !== 'undefined' && window.innerWidth < 600,
      recentDocuments: [],
      documentsLoading: false,
      documentsError: null,
      draftTransfers: [],
      transfersLoading: false,
      transfersError: null,
      warehouseBalances: [],
      warehouseBalanceSummary: { quantity: '0', total_cost: '0.00' },
      warehouseMovements: [],
      warehouseReports: {},
      warehouseDiagnostics: { summary: { status: 'ok', total: 0, by_code: {} }, issues: [] },
      warehouseLoading: {
        balances: false,
        movements: false,
        reports: false,
        diagnostics: false
      },
      warehouseError: null,
      reportingTasks: [],
      activeOrgId: null,
      requests: {
        orgInfo: null,
        userInfo: null,
        finance: null,
        financeRebuild: null,
        documents: null,
        transfers: null,
        warehouseBalances: null,
        warehouseMovements: null,
        warehouseReports: null,
        warehouseDiagnostics: null
      },
      quarterOptions: [
        { label: 'Весь год', value: 'all' },
        { label: 'I квартал', value: 'q1' },
        { label: 'II квартал', value: 'q2' },
        { label: 'III квартал', value: 'q3' },
        { label: 'IV квартал', value: 'q4' }
      ]
    }
  },
  computed: {
    yearOptions() {
      const currentYear = new Date().getFullYear()
      return [currentYear + 1, currentYear, currentYear - 1, currentYear - 2]
    },
    hasFinanceOverviewAccess() {
      return PERMISSION_KEYS.some((key) => this.permissions[key] === true)
    },
    hasFinDocumentAccess() {
      return this.permissions.open_fin_documents === true || this.permissions.edit_fin_documents === true
    },
    hasStockAccess() {
      return STOCK_PERMISSION_KEYS.some((key) => this.permissions[key] === true)
    },
    financeFreshnessState() {
      if (!this.hasFinanceOverviewAccess) return 'restricted'
      if (this.financeLoading && !this.financeSummary) return 'loading'
      if (this.financeError) return 'error'
      if (!this.financeSummary) return 'unavailable'
      if (this.financeSummary.is_stale) return 'stale'
      if (!this.hasSelectedFinanceMovement) return 'empty'
      return 'ready'
    },
    financeFreshnessLabel() {
      return {
        restricted: 'Финансы недоступны',
        loading: 'Данные загружаются',
        error: 'Данные недоступны',
        unavailable: 'Данные не подготовлены',
        stale: 'Требует обновления',
        empty: 'Движений нет',
        ready: 'Данные актуальны'
      }[this.financeFreshnessState]
    },
    financeFreshnessDetail() {
      const calculatedAt = this.financeSummary && this.financeSummary.calculated_at
      return calculatedAt ? `Обновлено ${this.formatDateTime(calculatedAt)}` : this.financeFreshnessLabel
    },
    hasOperationalOverview() {
      return this.hasStockAccess || this.hasFinDocumentAccess
    },
    attentionItemCount() {
      let count = 0
      if (this.stockDiagnosticIssueCount > 0) count += 1
      if (this.lowStockRows.length > 0) count += 1
      if (this.draftTransfers.length > 0) count += 1
      if (this.warehouseError) count += 1
      if (this.transfersError) count += 1
      return count + Math.min(this.reportingTasks.length, 3)
    },
    attentionSummary() {
      return this.attentionItemCount > 0
        ? `Рабочих исключений и ближайших сроков: ${this.attentionItemCount}.`
        : 'По доступным данным исключений не найдено.'
    },
    hasRecentEventsAccess() {
      return this.hasFinDocumentAccess || this.hasStockAccess
    },
    recentEventCount() {
      return Math.min(this.recentDocuments.length, 5) + this.recentStockMovements.length
    },
    recentEventsSummary() {
      return this.recentEventCount > 0
        ? `Последние документы и складские движения: ${this.recentEventCount}.`
        : 'Новых документов и складских движений нет.'
    },
    warehouseWidgetsLoading() {
      return this.warehouseLoading.balances || this.warehouseLoading.movements || this.warehouseLoading.reports || this.warehouseLoading.diagnostics
    },
    warehouseStockItemCount() {
      return this.warehouseBalances.length
    },
    warehousePositionCountLabel() {
      const count = this.warehouseStockItemCount
      const lastTwoDigits = count % 100
      const lastDigit = count % 10
      const suffix = lastTwoDigits >= 11 && lastTwoDigits <= 14
        ? 'позиций'
        : (lastDigit === 1 ? 'позиция' : (lastDigit >= 2 && lastDigit <= 4 ? 'позиции' : 'позиций'))
      return `${count} ${suffix}`
    },
    warehouseTotalQuantity() {
      return this.warehouseBalanceSummary.quantity || '0'
    },
    warehouseTotalCost() {
      return this.formatMoney(this.warehouseBalanceSummary.total_cost || 0, true)
    },
    reportLowStockRows() {
      const report = this.warehouseReports.low_zero_stock || {}
      return Array.isArray(report.rows) ? report.rows : []
    },
    lowStockRows() {
      const reportRows = this.reportLowStockRows.filter((row) => row.low_stock === true || Number(row.quantity || 0) <= Number(row.stock_threshold || row.reorder_threshold || 0))
      if (reportRows.length) {
        return reportRows
      }
      return this.warehouseBalances.filter((row) => row.low_stock === true)
    },
    recentStockMovements() {
      return this.warehouseMovements.slice(0, 4)
    },
    stockDiagnosticIssues() {
      return Array.isArray(this.warehouseDiagnostics.issues) ? this.warehouseDiagnostics.issues : []
    },
    stockDiagnosticIssueCount() {
      const summary = this.warehouseDiagnostics.summary || {}
      return Number(summary.total || this.stockDiagnosticIssues.length || 0)
    },
    selectedMonths() {
      return QUARTER_MONTHS[this.selectedQuarter] || QUARTER_MONTHS.all
    },
    selectedPeriodLabel() {
      const option = this.quarterOptions.find((item) => item.value === this.selectedQuarter)
      return `${option ? option.label : 'Весь год'} · ${this.selectedYear}`
    },
    selectedMonthlyRows() {
      const rows = this.financeSummary && Array.isArray(this.financeSummary.monthly_series)
        ? this.financeSummary.monthly_series
        : []
      return rows.filter((row) => this.selectedMonths.includes(parseInt(row.month, 10)))
    },
    kpiCards() {
      const values = this.selectedQuarter === 'all'
        ? this.yearKpiValues()
        : this.quarterKpiValues()
      return [
        { key: 'cash_balance', label: 'Баланс денег', value: values.cash_balance },
        { key: 'revenue', label: 'Выручка', value: values.revenue },
        { key: 'expenses_total', label: 'Расходы', value: values.expenses_total },
        { key: 'profit_before_tax', label: 'Прибыль до налогообложения', value: values.profit_before_tax },
        { key: 'net_profit', label: 'Чистая прибыль', value: values.net_profit }
      ].map((card) => ({
        ...card,
        display: this.financeSummary ? this.formatMoney(card.value) : '—'
      }))
    },
    chartRows() {
      return this.selectedMonthlyRows.map((row) => ({
        month: parseInt(row.month, 10),
        label: MONTH_LABELS[parseInt(row.month, 10) - 1],
        revenue: this.parseMoney(row.revenue),
        expenses: this.parseMoney(row.expenses_total)
      }))
    },
    chartHasData() {
      return this.chartRows.some((row) => row.revenue !== 0 || row.expenses !== 0)
    },
    hasSelectedFinanceMovement() {
      return this.selectedMonthlyRows.some((row) => {
        return [
          'revenue',
          'expenses_total',
          'profit_before_tax',
          'net_profit',
          'cash_income',
          'cash_outcome',
          'cash_net',
          'cash_balance_end'
        ].some((field) => this.parseMoney(row[field]) !== 0)
      })
    },
    chartSummary() {
      const values = this.kpiCards.reduce((result, card) => {
        result[card.key] = card.display
        return result
      }, {})
      return `Доходы и расходы по месяцам за период ${this.selectedPeriodLabel}. Выручка за период: ${values.revenue}; расходы: ${values.expenses_total}.`
    },
    staleMessage() {
      const diagnostics = this.financeSummary && Array.isArray(this.financeSummary.diagnostics)
        ? this.financeSummary.diagnostics
        : []
      const codes = [...new Set(diagnostics.map((item) => item.code).filter(Boolean))]
      const labels = codes.map((code) => this.diagnosticLabel(code)).filter(Boolean)
      return labels.length
        ? `Финансовые данные требуют внимания: ${labels.join(', ')}.`
        : 'Финансовые данные устарели или отсутствуют.'
    }
  },
  watch: {
    selectedYear() {
      this.loadFinanceData()
      this.refreshReportingTasks()
    },
    selectedQuarter() {
      this.$nextTick(this.renderFinanceChart)
    }
  },
  created() {
    moment.locale('ru')
    this.emitter.on('didReceiveUserInfo', this.onDidReceiveUserInfo)
    this.emitter.on('organisationInfoWasReceived', this.onOrgInfo)
    this.emitter.on('didReceoveUserInfoAcrossOrg', this.onUserInfoAcrossOrg)
    this.emitter.on('onOrganisationFinanceSummary', this.onFinanceSummary)
    this.emitter.on('onOrganisationFinanceAggregatesRebuilt', this.onFinanceAggregatesRebuilt)
    this.emitter.on('didReceiveDocumentsList', this.onDocumentsList)
    this.emitter.on('onListTransfers', this.onListTransfers)
    this.emitter.on('onGetStockBalances', this.onHomeStockBalances)
    this.emitter.on('onGetStockMovements', this.onHomeStockMovements)
    this.emitter.on('onGetStockReports', this.onHomeStockReports)
    this.emitter.on('onGetStockDiagnostics', this.onHomeStockDiagnostics)
    this.emitter.on('accessDeniedErrorReceived', this.onAccessDenied)

    if (this.connection.isAuthenticated) {
      this.loadDashboardData()
    } else {
      this.emitter.on('connectionReady', this.onConnectionReady)
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateCompactViewport)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateCompactViewport)
    this.emitter.off('didReceiveUserInfo', this.onDidReceiveUserInfo)
    this.emitter.off('organisationInfoWasReceived', this.onOrgInfo)
    this.emitter.off('didReceoveUserInfoAcrossOrg', this.onUserInfoAcrossOrg)
    this.emitter.off('onOrganisationFinanceSummary', this.onFinanceSummary)
    this.emitter.off('onOrganisationFinanceAggregatesRebuilt', this.onFinanceAggregatesRebuilt)
    this.emitter.off('didReceiveDocumentsList', this.onDocumentsList)
    this.emitter.off('onListTransfers', this.onListTransfers)
    this.emitter.off('onGetStockBalances', this.onHomeStockBalances)
    this.emitter.off('onGetStockMovements', this.onHomeStockMovements)
    this.emitter.off('onGetStockReports', this.onHomeStockReports)
    this.emitter.off('onGetStockDiagnostics', this.onHomeStockDiagnostics)
    this.emitter.off('accessDeniedErrorReceived', this.onAccessDenied)
    this.emitter.off('connectionReady', this.onConnectionReady)
    this.destroyFinanceChart()
  },
  methods: {
    updateCompactViewport() {
      const isCompact = window.innerWidth < 600
      if (isCompact === this.isCompactViewport) return
      this.isCompactViewport = isCompact
      if (isCompact) {
        this.destroyFinanceChart()
      } else {
        this.$nextTick(this.renderFinanceChart)
      }
    },
    kpiStateLabel(card) {
      if (this.financeLoading && !this.financeSummary) return 'Обновляем'
      if (!this.financeSummary) return 'Недоступно'
      if (!this.hasSelectedFinanceMovement) return 'Движений нет'
      return card.value === 0 ? 'Нулевое значение' : 'По данным учета'
    },
    onConnectionReady() {
      if (!this.requests.orgInfo) {
        this.loadDashboardData()
      }
    },
    makeRequestId(prefix) {
      if (this.connection && typeof this.connection.makeRequestId === 'function') {
        return this.connection.makeRequestId(prefix)
      }
      return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`
    },
    requestMatches(data, requestId) {
      if (!requestId) {
        return false
      }
      return data.request_id === requestId || data.requesrt_id === requestId || data.id === requestId
    },
    normalizeOrgId(orgId) {
      if (orgId === undefined || orgId === null || orgId === '' || orgId === -1 || orgId === '-1') {
        return null
      }
      return orgId
    },
    currentOrgId() {
      const orgId = this.normalizeOrgId(this.connection.org_id) || this.normalizeOrgId(this.connection.selectedOrgId(this.$q))
      return orgId
    },
    ensureOrgFromUserInfo(data) {
      const existingOrgId = this.currentOrgId()
      if (existingOrgId) {
        return existingOrgId
      }
      const organisations = Array.isArray(data && data.organisations) ? data.organisations : []
      if (!organisations.length) {
        return null
      }
      const orgId = organisations[0].id
      this.connection.updateOrgId(this.$q, orgId)
      return orgId
    },
    onDidReceiveUserInfo(data) {
      const orgId = this.ensureOrgFromUserInfo(data)
      if (orgId && !this.requests.orgInfo && this.connection.isAuthenticated) {
        this.loadDashboardData()
      }
    },
    loadDashboardData() {
      const orgId = this.currentOrgId()
      if (!orgId) {
        this.isLoading = false
        this.financeError = 'Организация не выбрана.'
        return
      }
      this.activeOrgId = orgId
      this.isLoading = true
      this.requests.orgInfo = this.makeRequestId('home-org')
      this.requests.userInfo = this.makeRequestId('home-user')
      this.connection.getOrganisationInfo(this.requests.orgInfo, orgId)
      this.connection.getUserInfoAcrossOrg(this.requests.userInfo, orgId)
    },
    loadFinanceData() {
      if (!this.hasFinanceOverviewAccess) {
        this.financeSummary = null
        this.financeError = null
        this.destroyFinanceChart()
        return
      }
      const orgId = this.currentOrgId()
      if (!orgId) {
        this.financeSummary = null
        this.financeError = 'Организация не выбрана.'
        this.destroyFinanceChart()
        return
      }
      this.financeLoading = true
      this.financeError = null
      this.requests.finance = this.makeRequestId('home-finance')
      this.connection.getOrganisationFinanceSummary(this.requests.finance, this.selectedYear, orgId)

      if (this.hasFinDocumentAccess) {
        this.loadRecentDocuments()
        this.loadDraftTransfers()
      }
    },
    rebuildFinanceAggregates() {
      if (this.financeRebuildLoading) {
        return
      }
      const orgId = this.currentOrgId()
      if (!orgId) {
        this.financeError = 'Организация не выбрана.'
        return
      }
      this.financeRebuildLoading = true
      this.financeError = null
      this.requests.financeRebuild = this.makeRequestId('home-finance-rebuild')
      this.connection.rebuildOrganisationFinanceAggregates(this.requests.financeRebuild, this.selectedYear, orgId)
    },
    loadRecentDocuments() {
      this.documentsLoading = true
      this.documentsError = null
      this.requests.documents = this.makeRequestId('home-documents')
      this.connection.getDocumentsList(
        this.requests.documents,
        moment(`${this.selectedYear}-01-01`).startOf('day').unix(),
        moment(`${this.selectedYear}-12-31`).endOf('day').unix(),
        'all',
        undefined,
        true,
        undefined,
        5,
        undefined,
        'id_desc'
      )
    },
    loadDraftTransfers() {
      this.transfersLoading = true
      this.transfersError = null
      this.requests.transfers = this.makeRequestId('home-transfers')
      this.connection.listTransfers(this.requests.transfers, {
        period_start: `${this.selectedYear}-01-01`,
        period_end: `${this.selectedYear}-12-31`,
        status: 'draft',
        page: 1,
        page_size: 5
      })
    },
    resetWarehouseWidgets() {
      this.warehouseBalances = []
      this.warehouseBalanceSummary = { quantity: '0', total_cost: '0.00' }
      this.warehouseMovements = []
      this.warehouseReports = {}
      this.warehouseDiagnostics = { summary: { status: 'ok', total: 0, by_code: {} }, issues: [] }
      this.warehouseLoading = {
        balances: false,
        movements: false,
        reports: false,
        diagnostics: false
      }
      this.warehouseError = null
    },
    loadWarehouseWidgets() {
      if (!this.hasStockAccess) {
        this.resetWarehouseWidgets()
        return
      }
      const orgId = this.currentOrgId()
      if (!orgId) {
        this.resetWarehouseWidgets()
        this.warehouseError = 'Организация не выбрана.'
        return
      }
      this.warehouseError = null
      this.warehouseLoading = {
        balances: true,
        movements: true,
        reports: true,
        diagnostics: true
      }
      this.requests.warehouseBalances = this.makeRequestId('home-stock-balances')
      this.requests.warehouseMovements = this.makeRequestId('home-stock-movements')
      this.requests.warehouseReports = this.makeRequestId('home-stock-reports')
      this.requests.warehouseDiagnostics = this.makeRequestId('home-stock-diagnostics')
      this.connection.getStockBalances(this.requests.warehouseBalances)
      this.connection.getStockMovements(this.requests.warehouseMovements, { page_size: 5 })
      this.connection.getStockReports(this.requests.warehouseReports, { low_stock_threshold: '2' })
      this.connection.getStockDiagnostics(this.requests.warehouseDiagnostics)
    },
    refreshReportingTasks() {
      this.reportingTasks = this.hasFinDocumentAccess
        ? getUpcomingReportingTasks(new Date(), { year: this.selectedYear })
        : []
    },
    onOrgInfo(data) {
      if (!this.requestMatches(data, this.requests.orgInfo)) {
        return
      }
      this.isLoading = false
      const meta = data.organisation_metadata || data
      const fullData = meta.full_data || {}
      this.orgInfo = {
        name: meta.display_name || meta.name || fullData.name?.full_with_opf || '',
        inn: meta.inn || fullData.inn || '',
        ogrn: fullData.ogrn || '',
        opf: fullData.opf?.full || '',
        address: fullData.address?.unrestricted_value || fullData.address?.value || '',
        director: fullData.management
          ? `${fullData.management.post || ''} ${fullData.management.name || ''}`.trim()
          : (fullData.fio
            ? `${fullData.fio.surname || ''} ${fullData.fio.name || ''} ${fullData.fio.patronymic || ''}`.trim()
            : ''),
        okved: fullData.okved || ''
      }
    },
    onUserInfoAcrossOrg(data) {
      const responseOrgId = data.current_org || data.organisation_id
      if (!this.requestMatches(data, this.requests.userInfo)) {
        return
      }
      if (responseOrgId && String(responseOrgId) !== String(this.currentOrgId())) {
        return
      }
      this.isLoading = false
      if (data.user && data.user.role) {
        this.userRole = data.user.role
        this.permissions = data.user.role.permissions || {}
      } else {
        this.userRole = null
        this.permissions = {}
      }
      const meta = data.organisation_metadata
      if (meta && !this.orgInfo.name) {
        this.orgInfo = {
          ...this.orgInfo,
          name: meta.display_name || meta.name || '',
          inn: meta.inn || this.orgInfo.inn || ''
        }
      }
      this.refreshReportingTasks()
      this.loadFinanceData()
      this.loadWarehouseWidgets()
    },
    onFinanceSummary(data) {
      if (!this.requestMatches(data, this.requests.finance)) {
        return
      }
      this.financeLoading = false
      if (data.type === 'error' || data.error) {
        this.financeSummary = null
        this.financeError = data.message || 'Финансовые данные недоступны.'
        this.destroyFinanceChart()
        return
      }
      this.financeSummary = data
      this.financeError = null
      this.$nextTick(this.renderFinanceChart)
    },
    onFinanceAggregatesRebuilt(data) {
      if (!this.requestMatches(data, this.requests.financeRebuild)) {
        return
      }
      this.financeRebuildLoading = false
      this.requests.financeRebuild = null
      if (data.type === 'error' || data.error) {
        const message = data.message || 'Не удалось обновить финансовые данные.'
        this.financeError = message
        this.$q.notify({
          type: 'negative',
          message: message
        })
        return
      }
      this.$q.notify({
        type: 'positive',
        message: 'Финансовые данные обновлены.'
      })
      this.loadFinanceData()
    },
    onDocumentsList(data) {
      if (!this.requestMatches(data, this.requests.documents)) {
        return
      }
      this.documentsLoading = false
      if (data.type === 'error' || data.error) {
        this.recentDocuments = []
        this.documentsError = data.message || 'Не удалось загрузить документы.'
        return
      }
      this.documentsError = null
      this.recentDocuments = Array.isArray(data.data) ? data.data : []
    },
    onListTransfers(data) {
      if (!this.requestMatches(data, this.requests.transfers)) {
        return
      }
      this.transfersLoading = false
      if (data.type === 'error' || data.error) {
        this.draftTransfers = []
        this.transfersError = data.message || 'Не удалось загрузить проводки.'
        return
      }
      this.transfersError = null
      this.draftTransfers = Array.isArray(data.transfers) ? data.transfers : []
    },
    setWarehouseError(data, fallback) {
      this.warehouseError = data.message || (data.error_info && data.error_info.message) || fallback
    },
    onHomeStockBalances(data) {
      if (!this.requestMatches(data, this.requests.warehouseBalances)) {
        return
      }
      this.warehouseLoading.balances = false
      if (data.type === 'error' || data.error) {
        this.warehouseBalances = []
        this.warehouseBalanceSummary = { quantity: '0', total_cost: '0.00' }
        this.setWarehouseError(data, 'Не удалось загрузить остатки склада.')
        return
      }
      this.warehouseBalances = Array.isArray(data.balances) ? data.balances : []
      this.warehouseBalanceSummary = data.summary || { quantity: '0', total_cost: '0.00' }
    },
    onHomeStockMovements(data) {
      if (!this.requestMatches(data, this.requests.warehouseMovements)) {
        return
      }
      this.warehouseLoading.movements = false
      if (data.type === 'error' || data.error) {
        this.warehouseMovements = []
        this.setWarehouseError(data, 'Не удалось загрузить движения склада.')
        return
      }
      this.warehouseMovements = Array.isArray(data.movements) ? data.movements : []
    },
    onHomeStockReports(data) {
      if (!this.requestMatches(data, this.requests.warehouseReports)) {
        return
      }
      this.warehouseLoading.reports = false
      if (data.type === 'error' || data.error) {
        this.warehouseReports = {}
        this.setWarehouseError(data, 'Не удалось загрузить отчеты склада.')
        return
      }
      this.warehouseReports = data.reports || {}
    },
    onHomeStockDiagnostics(data) {
      if (!this.requestMatches(data, this.requests.warehouseDiagnostics)) {
        return
      }
      this.warehouseLoading.diagnostics = false
      if (data.type === 'error' || data.error) {
        this.warehouseDiagnostics = { summary: { status: 'ok', total: 0, by_code: {} }, issues: [] }
        this.setWarehouseError(data, 'Не удалось загрузить диагностику склада.')
        return
      }
      this.warehouseDiagnostics = {
        summary: data.summary || { status: 'ok', total: 0, by_code: {} },
        issues: Array.isArray(data.issues) ? data.issues : []
      }
    },
    onAccessDenied(data) {
      if (this.requestMatches(data, this.requests.finance)) {
        this.financeLoading = false
        this.financeSummary = null
        this.financeError = 'Нет прав на просмотр финансовых данных.'
      }
      if (this.requestMatches(data, this.requests.financeRebuild)) {
        this.financeRebuildLoading = false
        this.financeError = 'Нет прав на обновление финансовых данных.'
      }
      if (this.requestMatches(data, this.requests.documents)) {
        this.documentsLoading = false
        this.documentsError = 'Нет прав на просмотр документов.'
      }
      if (this.requestMatches(data, this.requests.transfers)) {
        this.transfersLoading = false
        this.transfersError = 'Нет прав на просмотр проводок.'
      }
      if ([
        this.requests.warehouseBalances,
        this.requests.warehouseMovements,
        this.requests.warehouseReports,
        this.requests.warehouseDiagnostics
      ].some((requestId) => this.requestMatches(data, requestId))) {
        this.warehouseLoading = {
          balances: false,
          movements: false,
          reports: false,
          diagnostics: false
        }
        this.warehouseError = 'Нет прав на просмотр склада.'
      }
    },
    yearKpiValues() {
      const cards = this.financeSummary && this.financeSummary.cards ? this.financeSummary.cards : {}
      return {
        cash_balance: this.parseMoney(cards.cash_balance),
        revenue: this.parseMoney(cards.revenue),
        expenses_total: this.parseMoney(cards.expenses_total),
        profit_before_tax: this.parseMoney(cards.profit_before_tax),
        net_profit: this.parseMoney(cards.net_profit)
      }
    },
    quarterKpiValues() {
      const rows = this.selectedMonthlyRows
      const lastRow = rows[rows.length - 1] || {}
      return {
        cash_balance: this.parseMoney(lastRow.cash_balance_end),
        revenue: this.sumRows(rows, 'revenue'),
        expenses_total: this.sumRows(rows, 'expenses_total'),
        profit_before_tax: this.sumRows(rows, 'profit_before_tax'),
        net_profit: this.sumRows(rows, 'net_profit')
      }
    },
    sumRows(rows, field) {
      return rows.reduce((total, row) => total + this.parseMoney(row[field]), 0)
    },
    parseMoney(value) {
      if (value === null || value === undefined || value === '') {
        return 0
      }
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0
      }
      const parsed = parseFloat(String(value).replace(/\s/g, '').replace(',', '.'))
      return Number.isFinite(parsed) ? parsed : 0
    },
    formatMoney(value) {
      return formatDisplayMoney(value, { currency: 'RUB' })
    },
    formatDate(value) {
      if (!value) {
        return 'Без даты'
      }
      return formatDisplayDate(value, 'Без даты')
    },
    formatDateTime(value) {
      if (!value) {
        return ''
      }
      return formatDisplayDateTime(value)
    },
    documentTypeLabel(type) {
      return DOCUMENT_TYPE_LABELS[type] || 'Документ'
    },
    directionLabel(direction) {
      if (direction === 'in') return 'Входящий'
      if (direction === 'out') return 'Исходящий'
      if (direction === 'internal') return 'Внутренний'
      return 'Без направления'
    },
    diagnosticLabel(code) {
      const labels = {
        aggregate_missing: 'данные не подготовлены',
        aggregate_dirty: 'данные устарели',
        aggregate_rebuild_failed: 'не удалось обновить данные'
      }
      return labels[code] || code
    },
    reportStatusLabel(status) {
      if (status === 'supported') {
        return 'Доступно'
      }
      return 'Не настроено'
    },
    reportStatusColor(status) {
      if (status === 'supported') {
        return 'positive'
      }
      return 'warning'
    },
    openDocument(document) {
      const routeName = DOCUMENT_ROUTES[document.document_type]
      if (!routeName) {
        this.$q.notify({
          type: 'info',
          message: 'Для этого типа документа нет карточки просмотра.'
        })
        return
      }
      this.$router.push({
        name: routeName,
        params: { id: document.id, history_id: 'last' }
      })
    },
    openTransfer(transfer) {
      if (transfer.document_id) {
        this.openDocument({
          id: transfer.document_id,
          document_type: transfer.document_type
        })
        return
      }
      this.$q.notify({
        type: 'info',
        message: 'Карточка проводки без документа пока недоступна.'
      })
    },
    warehouseName(row) {
      const warehouse = row && row.warehouse ? row.warehouse : {}
      return warehouse.name || row.warehouse_name || row.warehouse_id || 'Склад'
    },
    openStockMovement(movement) {
      const document = movement.document || {}
      const documentId = movement.document_id || document.id
      if (documentId) {
        this.openDocument({
          id: documentId,
          document_type: document.document_type || movement.document_type || 'upd'
        })
        return
      }
      this.$router.push({ name: 'store_movements' })
    },
    openReportTask(task) {
      if (task.routeName) {
        this.$router.push({ name: task.routeName })
        return
      }
      this.$q.notify({
        type: 'info',
        message: 'Форма отчета пока не настроена.'
      })
    },
    renderFinanceChart() {
      this.destroyFinanceChart()
      if (!this.chartHasData || !this.$refs.financeChart) {
        return
      }
      this.financeChart = new ApexCharts(this.$refs.financeChart, {
        chart: {
          type: 'bar',
          height: 320,
          toolbar: { show: false },
          fontFamily: 'inherit'
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: '42%'
          }
        },
        colors: [this.chartToken('--sid-color-success'), this.chartToken('--sid-color-danger')],
        dataLabels: { enabled: false },
        series: [
          {
            name: 'Доходы',
            data: this.chartRows.map((row) => row.revenue)
          },
          {
            name: 'Расходы',
            data: this.chartRows.map((row) => row.expenses)
          }
        ],
        xaxis: {
          categories: this.chartRows.map((row) => row.label)
        },
        yaxis: {
          labels: {
            formatter: (value) => this.formatMoney(value)
          }
        },
        tooltip: {
          y: {
            formatter: (value) => this.formatMoney(value, true)
          }
        },
        legend: {
          position: 'top',
          horizontalAlign: 'left'
        },
        grid: {
          borderColor: this.chartToken('--sid-color-line')
        }
      })
      this.financeChart.render()
    },
    chartToken(name) {
      return window.getComputedStyle(this.$refs.financeChart || document.documentElement).getPropertyValue(name).trim()
    },
    destroyFinanceChart() {
      if (this.financeChart) {
        this.financeChart.destroy()
        this.financeChart = null
      }
    }
  }
}
</script>

<style scoped lang="scss">
:deep(.sid-app-page__content),
.overview-context,
.overview-filters,
.kpi-grid,
.attention-list,
.recent-events,
.analysis-panel {
  min-width: 0;
}

.overview-context {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sid-space-2) var(--sid-space-5);
  color: var(--sid-color-text-secondary);
  font-size: var(--sid-font-size-body);
}

.overview-filters {
  display: flex;
  gap: var(--sid-space-2);
}

.overview-filter {
  width: 7.5rem;
}

.overview-filter--period {
  width: 10rem;
}

.freshness-badge {
  display: inline-flex;
  align-items: center;
  min-height: 1.75rem;
  padding: var(--sid-space-1) var(--sid-space-3);
  border-radius: var(--sid-radius-pill);
  background: var(--sid-color-surface-subtle);
  color: var(--sid-color-text-secondary);
  font-size: var(--sid-font-size-label);
  font-weight: var(--sid-font-weight-semibold);
}

.freshness-badge--ready {
  background: var(--sid-color-success-surface);
  color: var(--sid-color-success);
}

.freshness-badge--stale,
.freshness-badge--empty {
  background: var(--sid-color-warning-surface);
  color: var(--sid-color-warning);
}

.freshness-badge--error,
.freshness-badge--unavailable {
  background: var(--sid-color-danger-surface);
  color: var(--sid-color-danger);
}

.overview-message {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--sid-space-3);
  margin-bottom: var(--sid-space-4);
  padding: var(--sid-space-3) var(--sid-space-4);
  border: 1px solid var(--sid-color-line);
  border-radius: var(--sid-radius-surface);
  color: var(--sid-color-text-secondary);
}

.overview-message--error {
  border-color: var(--sid-color-danger);
  background: var(--sid-color-danger-surface);
  color: var(--sid-color-danger);
}

.overview-message--warning {
  border-color: var(--sid-color-warning);
  background: var(--sid-color-warning-surface);
}

.overview-message--neutral {
  background: var(--sid-color-info-surface);
  color: var(--sid-color-info);
}

.overview-message__content {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--sid-space-1);
}

.overview-message__content span {
  overflow-wrap: anywhere;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  border: 1px solid var(--sid-color-line);
  border-radius: var(--sid-radius-surface);
  overflow: hidden;
}

.kpi-item {
  display: flex;
  min-width: 0;
  min-height: 8rem;
  flex-direction: column;
  padding: var(--sid-space-4);
  border-right: 1px solid var(--sid-color-line);
  background: var(--sid-color-surface-0);
}

.kpi-item:last-child {
  border-right: 0;
}

.kpi-item--loading {
  gap: var(--sid-space-2);
}

.kpi-item__label,
.kpi-item__period,
.kpi-item__state {
  color: var(--sid-color-text-muted);
  font-size: var(--sid-font-size-label);
  line-height: var(--sid-line-height-label);
}

.kpi-item__value {
  margin: var(--sid-space-2) 0 auto;
  color: var(--sid-color-text-primary);
  font-size: 1.35rem;
  line-height: 1.75rem;
  overflow-wrap: anywhere;
}

.kpi-item__value--negative {
  color: var(--sid-color-danger);
}

.kpi-item__state {
  margin-top: var(--sid-space-1);
}

.warehouse-overview {
  margin-bottom: var(--sid-space-4);
  border: 1px solid var(--sid-color-line);
  border-radius: var(--sid-radius-surface);
  overflow: hidden;
}

.warehouse-overview__header,
.warehouse-balance {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sid-space-4);
  padding: var(--sid-space-3) var(--sid-space-4);
}

.warehouse-overview__header {
  border-bottom: 1px solid var(--sid-color-line);
  background: var(--sid-color-surface-canvas);
}

.warehouse-overview__header > div,
.warehouse-balance > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--sid-space-1);
}

.warehouse-overview__header span,
.warehouse-balance__label,
.warehouse-balance__meta,
.warehouse-balance__empty {
  color: var(--sid-color-text-muted);
  font-size: var(--sid-font-size-label);
}

.warehouse-balance__value {
  color: var(--sid-color-text-primary);
  font-size: var(--sid-font-size-section);
}

.attention-list,
.recent-events__list {
  border: 1px solid var(--sid-color-line);
  border-radius: var(--sid-radius-surface);
  overflow: hidden;
}

.attention-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: var(--sid-space-3);
  min-height: var(--sid-row-comfortable);
  padding: var(--sid-space-3) var(--sid-space-4);
  border-bottom: 1px solid var(--sid-color-line);
  color: var(--sid-color-text-primary);
  text-decoration: none;
}

.attention-item:last-child {
  border-bottom: 0;
}

a.attention-item:hover {
  background: var(--sid-color-surface-canvas);
}

a.attention-item:focus-visible,
.recent-event:focus-visible,
.recent-events summary:focus-visible {
  outline: var(--sid-focus-width) solid var(--sid-focus-color);
  outline-offset: calc(var(--sid-focus-offset) * -1);
}

.attention-item__marker {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: var(--sid-color-info);
}

.attention-item--danger .attention-item__marker {
  background: var(--sid-color-danger);
}

.attention-item--warning .attention-item__marker {
  background: var(--sid-color-warning);
}

.attention-item__body {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--sid-space-1);
}

.attention-item__body span {
  color: var(--sid-color-text-muted);
  font-size: var(--sid-font-size-label);
  overflow-wrap: anywhere;
}

.attention-item__count {
  min-width: 1.75rem;
  padding: var(--sid-space-1) var(--sid-space-2);
  border-radius: var(--sid-radius-pill);
  background: var(--sid-color-surface-subtle);
  text-align: center;
  font-weight: var(--sid-font-weight-semibold);
}

.attention-item__action {
  color: var(--sid-color-primary);
  font-weight: var(--sid-font-weight-medium);
}

.attention-empty {
  display: flex;
  align-items: center;
  gap: var(--sid-space-3);
  min-height: 5rem;
  padding: var(--sid-space-4);
  color: var(--sid-color-success);
}

.attention-empty > div {
  display: flex;
  flex-direction: column;
}

.recent-events summary {
  display: flex;
  align-items: center;
  min-height: var(--sid-touch-target);
  padding: 0 var(--sid-space-4);
  cursor: pointer;
  color: var(--sid-color-text-secondary);
  font-weight: var(--sid-font-weight-medium);
}

.recent-events[open] summary {
  border-bottom: 1px solid var(--sid-color-line);
}

.recent-event {
  display: grid;
  width: 100%;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--sid-space-3);
  min-height: var(--sid-row-comfortable);
  padding: var(--sid-space-3) var(--sid-space-4);
  border: 0;
  border-bottom: 1px solid var(--sid-color-line);
  background: var(--sid-color-surface-0);
  color: var(--sid-color-text-primary);
  cursor: pointer;
  font: inherit;
  text-align: left;
}

.recent-event:hover {
  background: var(--sid-color-surface-canvas);
}

.recent-event:last-child {
  border-bottom: 0;
}

.recent-event__icon {
  color: var(--sid-color-text-muted);
}

.recent-event__body {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--sid-space-1);
}

.recent-event__body span,
.recent-event__meta,
.recent-event--status {
  color: var(--sid-color-text-muted);
  font-size: var(--sid-font-size-label);
}

.recent-event__body span,
.recent-event__meta {
  overflow-wrap: anywhere;
}

.recent-event--status {
  display: flex;
  cursor: default;
}

.analysis-panel {
  border: 1px solid var(--sid-color-line);
  border-radius: var(--sid-radius-surface);
  overflow: hidden;
}

.finance-chart {
  min-height: 20rem;
  padding: var(--sid-space-2);
}

.chart-skeleton {
  padding: var(--sid-space-4);
}

.chart-summary-visible {
  margin: 0;
  padding: var(--sid-space-4);
  color: var(--sid-color-text-secondary);
  font-size: var(--sid-font-size-body);
  line-height: var(--sid-line-height-body);
}

.chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sid-space-3);
  min-height: 12rem;
  padding: var(--sid-space-5);
  color: var(--sid-color-text-muted);
  text-align: left;
}

.chart-empty > div {
  display: flex;
  flex-direction: column;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 1100px) {
  .kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .kpi-item {
    border-bottom: 1px solid var(--sid-color-line);
  }

  .kpi-item:nth-child(3n) {
    border-right: 0;
  }

  .kpi-item:nth-last-child(-n + 2) {
    border-bottom: 0;
  }
}

@media (max-width: 599px) {
  .overview-filters,
  .warehouse-overview__header,
  .warehouse-balance {
    align-items: stretch;
    flex-direction: column;
  }

  .overview-filter,
  .overview-filter--period {
    width: 100%;
  }

  .overview-message {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .overview-message :deep(.q-btn) {
    grid-column: 1 / -1;
    justify-self: stretch;
    min-height: var(--sid-touch-target);
  }

  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .kpi-item,
  .kpi-item:nth-child(3n) {
    min-height: 7.5rem;
    border-right: 1px solid var(--sid-color-line);
    border-bottom: 1px solid var(--sid-color-line);
  }

  .kpi-item:nth-child(2n) {
    border-right: 0;
  }

  .kpi-item:last-child {
    grid-column: 1 / -1;
    border-right: 0;
    border-bottom: 0;
  }

  .attention-item {
    grid-template-columns: auto minmax(0, 1fr) auto;
  }

  .attention-item__action {
    grid-column: 2 / -1;
  }

  .recent-event {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .recent-event__meta {
    grid-column: 2;
  }

  .chart-empty {
    align-items: flex-start;
  }
}
</style>
